import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Observable } from 'rxjs';
import { ApiEndpointsService } from 'src/app/core/services/api-endpoints.service';
import { ApiHttpService } from 'src/app/core/services/api-http.service';
import {
  Counterpart,
  CounterpartApiListResponse,
  CounterpartBankAccount,
  CounterpartBankAccountForm,
  CounterpartContact,
  CounterpartContactForm,
  CounterpartDetailState,
  CounterpartForm,
  CounterpartPayload,
  CounterpartVatId,
  CounterpartVatIdForm,
} from './counterpart-detail.model';

@Injectable({
  providedIn: 'root',
})
export class CounterpartDetailService {
  public openDrawerFlag: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  private counterpartDetails: BehaviorSubject<CounterpartDetailState | null> =
    new BehaviorSubject<CounterpartDetailState | null>(null);
  public counterpartListRefresh: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  counterpartDetailsObservable$ = this.counterpartDetails.asObservable();
  openDrawerObservable$ = this.openDrawerFlag.asObservable();
  counterpartListRefreshObservable$ =
    this.counterpartListRefresh.asObservable();

  constructor(
    private apiHttpService: ApiHttpService,
    private apiEndpointsService: ApiEndpointsService
  ) {}

  public openDrawer(): void {
    this.openDrawerFlag.next(true);
  }

  public closeDrawer(): void {
    this.openDrawerFlag.value && this.openDrawerFlag.next(false);
  }

  public setCounterpartDetails(data: CounterpartDetailState | null): void {
    !this.openDrawerFlag.value && this.counterpartDetails.next(data);
  }

  public setCounterpartListRefresh(data: boolean): void {
    this.counterpartListRefresh.next(data);
  }

  getCounterpartBankAccounts(counterpartId: string): Observable<CounterpartApiListResponse<CounterpartBankAccount>> {
    return this.apiHttpService.get(
      this.apiEndpointsService.createUrlV2(
        `counterparties/${counterpartId}/bank-accounts`,
        false
      )
    ) as unknown as Observable<CounterpartApiListResponse<CounterpartBankAccount>>;
  }

  getCounterpartContacts(counterpartId: string): Observable<CounterpartApiListResponse<CounterpartContact>> {
    return this.apiHttpService.get(
      this.apiEndpointsService.createUrlV2(
        `counterparties/${counterpartId}/contact-persons`,
        false
      )
    ) as unknown as Observable<CounterpartApiListResponse<CounterpartContact>>;
  }

  getCounterpartVatIds(counterpartId: string): Observable<CounterpartApiListResponse<CounterpartVatId>> {
    return this.apiHttpService.get(
      this.apiEndpointsService.createUrlV2(
        `counterparties/${counterpartId}/tax-info`,
        false
      )
    ) as unknown as Observable<CounterpartApiListResponse<CounterpartVatId>>;
  }

  createCounterpart(vehicleId: string, data: CounterpartPayload<CounterpartForm>): Observable<Counterpart> {
    return this.apiHttpService.post(
      this.apiEndpointsService.createUrlV2(
        `vehicles/${vehicleId}/counterparties`,
        false
      ),
      data
    ) as unknown as Observable<Counterpart>;
  }

  createCounterpartContact(
    counterpartId: string,
    data: CounterpartPayload<CounterpartContactForm>
  ): Observable<CounterpartContact> {
    return this.apiHttpService.post(
      this.apiEndpointsService.createUrlV2(
        `counterparties/${counterpartId}/contact-persons`,
        false
      ),
      data
    ) as unknown as Observable<CounterpartContact>;
  }

  createVatId(counterpartId: string, data: CounterpartPayload<CounterpartVatIdForm>): Observable<CounterpartVatId> {
    return this.apiHttpService.post(
      this.apiEndpointsService.createUrlV2(
        `counterparties/${counterpartId}/tax-info`,
        false
      ),
      data
    ) as unknown as Observable<CounterpartVatId>;
  }

  createBankAccount(
    counterpartId: string,
    data: CounterpartPayload<CounterpartBankAccountForm>
  ): Observable<CounterpartBankAccount> {
    return this.apiHttpService.post(
      this.apiEndpointsService.createUrlV2(
        `counterparties/${counterpartId}/bank-accounts`,
        false
      ),
      data
    ) as unknown as Observable<CounterpartBankAccount>;
  }

  makeDefaultBankaccount(
    counterpartId: string,
    bankAccountId: string
  ): Observable<unknown> {
    return this.apiHttpService.post(
      this.apiEndpointsService.createUrlV2(
        `counterparties/${counterpartId}/bank-accounts/${bankAccountId}/make-default`,
        false
      ),
      {}
    ) as unknown as Observable<unknown>;
  }

  makeDefaultContact(
    counterpartId: string,
    contactId: string
  ): Observable<unknown> {
    return this.apiHttpService.post(
      this.apiEndpointsService.createUrlV2(
        `counterparties/${counterpartId}/contact-persons/${contactId}/make-primary`,
        false
      ),
      {}
    ) as unknown as Observable<unknown>;
  }

  updateCounterpart(
    vehicleId: string,
    counterpartId: string,
    data: CounterpartPayload<CounterpartForm>
  ): Observable<Counterpart> {
    return this.apiHttpService.put(
      this.apiEndpointsService.createUrlV2(
        `vehicles/${vehicleId}/counterparties/${counterpartId}`,
        false
      ),
      data
    ) as unknown as Observable<Counterpart>;
  }

  updateVatId(
    counterpartId: string,
    vatId: string,
    data: CounterpartPayload<CounterpartVatIdForm>
  ): Observable<CounterpartVatId> {
    return this.apiHttpService.put(
      this.apiEndpointsService.createUrlV2(
        `counterparties/${counterpartId}/tax-info/${vatId}`,
        false
      ),
      data
    ) as unknown as Observable<CounterpartVatId>;
  }

  updateContact(
    counterpartId: string,
    contactId: string,
    data: CounterpartPayload<CounterpartContactForm>
  ): Observable<CounterpartContact> {
    return this.apiHttpService.put(
      this.apiEndpointsService.createUrlV2(
        `counterparties/${counterpartId}/contact-persons/${contactId}`,
        false
      ),
      data
    ) as unknown as Observable<CounterpartContact>;
  }

  updateBankAccount(
    counterpartId: string,
    bankAccountId: string,
    data: CounterpartPayload<CounterpartBankAccountForm>
  ): Observable<CounterpartBankAccount> {
    return this.apiHttpService.put(
      this.apiEndpointsService.createUrlV2(
        `counterparties/${counterpartId}/bank-accounts/${bankAccountId}`,
        false
      ),
      data
    ) as unknown as Observable<CounterpartBankAccount>;
  }

  deleteCounterpart(vehicleId: string, counterpartId: string): Observable<unknown> {
    return this.apiHttpService.delete(
      this.apiEndpointsService.createUrlV2(
        `vehicles/${vehicleId}/counterparties/${counterpartId}`,
        false
      )
    ) as unknown as Observable<unknown>;
  }

  deleteBankAccount(
    counterpartId: string,
    bankAccountId: string
  ): Observable<unknown> {
    return this.apiHttpService.delete(
      this.apiEndpointsService.createUrlV2(
        `counterparties/${counterpartId}/bank-accounts/${bankAccountId}`,
        false
      )
    ) as unknown as Observable<unknown>;
  }

  deleteContact(counterpartId: string, contactId: string): Observable<unknown> {
    return this.apiHttpService.delete(
      this.apiEndpointsService.createUrlV2(
        `counterparties/${counterpartId}/contact-persons/${contactId}`,
        false
      )
    ) as unknown as Observable<unknown>;
  }

  deleteVatId(counterpartId: string, vatId: string): Observable<unknown> {
    return this.apiHttpService.delete(
      this.apiEndpointsService.createUrlV2(
        `counterparties/${counterpartId}/tax-info/${vatId}`,
        false
      )
    ) as unknown as Observable<unknown>;
  }
}
